import { supportService } from "./support.service";
import { utilsService } from "./utils.service";
import { namespaceService } from "./namespace.service";
import {
    ElementOffset,
    HiddenDimensions,
    ParsedEffects,
    ShadowDimensions,
    ScrollResult,
    VerticalScrollResult,
    VerticalScrollOptions,
    ScrollToElementOptions,
    ProgressOptions,
    DragToScrollOptions,
    DragToScrollHandler,
    ResizeObserverServiceOptions
} from "../models/dom-utils";

// Constants
const PERCENT_REGEXP = /%/;
const BOX_SHADOW_REGEXP = /(\d+(?:\.?)\d*)px\s*(\d+(?:\.?)\d*)px\s*(\d+(?:\.?)\d*)px\s*(\d+)?/i;

/**
 * Service providing DOM utility functions
 */
class DomUtilsService {
    private animationQueue: Array<() => void> = [];
    private animationFrameFn: (callback: FrameRequestCallback) => void;

    constructor() {
        // Set up requestAnimationFrame with fallbacks
        const win = window as any;
        this.animationFrameFn =
            win.requestAnimationFrame ||
            win.webkitRequestAnimationFrame ||
            win.mozRequestAnimationFrame ||
            win.oRequestAnimationFrame ||
            win.msRequestAnimationFrame ||
            ((callback: FrameRequestCallback) => { setTimeout(callback, 1000 / 60); });
    }

    isElement(element: any): boolean {
        return element instanceof Element || element instanceof HTMLDocument;
    }

    /**
     * Get outer width of element
     */
    outerWidth(element: HTMLElement | JQuery | Window, includeMargin?: boolean, calculateFromHidden?: boolean): number {
        const $element = $(element as any);
        if (calculateFromHidden) {
            return this.getHiddenDimensions($element, includeMargin).width;
        }
        return $element.outerWidth(includeMargin || false) || 0;
    }

    /**
     * Get outer height of element
     */
    outerHeight(element: HTMLElement | JQuery | Window, includeMargin?: boolean, calculateFromHidden?: boolean): number {
        const $element = $(element as any);
        if (calculateFromHidden) {
            return this.getHiddenDimensions($element, includeMargin).height;
        }
        return $element.outerHeight(includeMargin || false) || 0;
    }

    /**
     * Get computed styles for an element
     */
    getComputedStyles(element: HTMLElement, properties?: string[]): Record<string, string> {
        const styles: Record<string, string> = {};
        let computedStyle: any;

        if (document.defaultView && document.defaultView.getComputedStyle) {
            computedStyle = document.defaultView.getComputedStyle(element, "");

            if (properties) {
                $.each(properties, (_idx: number, value: string) => {
                    styles[value] = computedStyle.getPropertyValue(value);
                });
            }
        } else {
            // IE fallback
            computedStyle = (element as any).currentStyle;

            if (properties) {
                $.each(properties, (_idx: number, value: string) => {
                    styles[value] = computedStyle[utilsService.toCamelCase(value)];
                });
            }
        }

        if (!utilsService.size(styles)) {
            return computedStyle;
        }

        return styles;
    }

    /**
     * Check if an element is scrollable
     */
    isScrollable(element: HTMLElement): boolean {
        const dataset = element.dataset as any;
        if (dataset[namespaceService.ns + "scrollable"] === "false") {
            return false;
        }

        if (typeof element.className === "string" &&
            element.className.indexOf("k-auto-scrollable") > -1) {
            return true;
        }

        const overflow = this.getComputedStyles(element, ["overflow"]).overflow || "";
        return overflow.indexOf("auto") > -1 || overflow.indexOf("scroll") > -1;
    }

    /**
     * Get or set scroll left position (RTL-aware)
     */
    scrollLeft(element: HTMLElement | JQuery, value?: number): number | void {
        const webkit = supportService.browser.webkit;
        const mozilla = supportService.browser.mozilla;
        const browserVersion = supportService.browser.version;

        // Handle jQuery collection
        if (element instanceof $ && value !== undefined) {
            (element as JQuery).each((_i: number, e: HTMLElement) => {
                this.scrollLeft(e, value);
            });
            return;
        }

        const el: HTMLElement = element instanceof $ ? (element as JQuery)[0] : element as HTMLElement;

        if (!el) {
            return;
        }

        const isRtl = supportService.isRtl(element);

        if (value !== undefined) {
            if (isRtl && webkit && (browserVersion < 85 || supportService.browser.safari)) {
                el.scrollLeft = el.scrollWidth - el.clientWidth - value;
            } else if (isRtl && (mozilla || webkit) && value > 0) {
                el.scrollLeft = -value;
            } else {
                el.scrollLeft = value;
            }
        } else if (isRtl && webkit && (browserVersion < 85 || supportService.browser.safari)) {
            return el.scrollWidth - el.clientWidth - el.scrollLeft;
        } else {
            return Math.abs(el.scrollLeft);
        }
    }

    /**
     * Get element offset position
     */
    getOffset(element: JQuery, type: string = "offset", positioned?: boolean): ElementOffset {
        const offset = (element as any)[type]();

        // Clone ClientRect object to JS object (jQuery3)
        const result: ElementOffset = {
            top: offset.top,
            right: offset.right,
            bottom: offset.bottom,
            left: offset.left
        };

        // IE10 touch zoom is living in a separate viewport
        if (supportService.browser.msie && (supportService.pointers || supportService.msPointers) && !positioned) {
            const sign = supportService.isRtl(element) ? 1 : -1;

            result.top -= (window.pageYOffset - (document.documentElement.scrollTop));
            result.left -= (window.pageXOffset + (sign * document.documentElement.scrollLeft));
        }

        return result;
    }

    /**
     * Get dimensions of a hidden element by temporarily cloning and showing it
     */
    getHiddenDimensions(element: JQuery, includeMargin?: boolean): HiddenDimensions {
        const clone = element.clone();
        clone.css("display", "");
        clone.css("visibility", "hidden");
        clone.appendTo($("body"));

        const width = clone.outerWidth(includeMargin || false);
        const height = clone.outerHeight(includeMargin || false);

        clone.remove();

        return {
            width: width || 0,
            height: height || 0
        };
    }

    /**
     * Parse effects string into object
     */
    parseEffects(input: string | string[]): ParsedEffects {
        const effects: ParsedEffects = {};
        const items = typeof input === "string" ? input.split(" ") : input;

        $.each(items, function (this: string, idx: number) {
            effects[idx] = this;
        });

        return effects;
    }

    /**
     * Remove whitespace text nodes from an element
     */
    stripWhitespace(element: HTMLElement): void {
        if (document.createNodeIterator) {
            const iterator = document.createNodeIterator(
                element,
                NodeFilter.SHOW_TEXT,
                (node: Node) => {
                    return node.parentNode === element
                        ? NodeFilter.FILTER_ACCEPT
                        : NodeFilter.FILTER_REJECT;
                }
            );

            while (iterator.nextNode()) {
                const refNode = (iterator as any).referenceNode;
                if (refNode && !refNode.textContent?.trim()) {
                    refNode.parentNode?.removeChild(refNode);
                }
            }
        }
    }

    /**
     * Request animation frame wrapper
     */
    animationFrame(callback: FrameRequestCallback): void {
        this.animationFrameFn.call(window, callback);
    }

    /**
     * Queue an animation callback
     */
    queueAnimation(callback: () => void): void {
        this.animationQueue.push(callback);
        if (this.animationQueue.length === 1) {
            this.runNextAnimation();
        }
    }

    /**
     * Run the next animation in queue
     */
    runNextAnimation(): void {
        this.animationFrame(() => {
            if (this.animationQueue[0]) {
                this.animationQueue.shift()!();
                if (this.animationQueue[0]) {
                    this.runNextAnimation();
                }
            }
        });
    }

    /**
     * Parse query string parameters from URL
     */
    parseQueryStringParams(url: string): Record<string, string> {
        const queryString = url.split("?")[1] || "";
        const params: Record<string, string> = {};
        const paramParts = queryString.split(/&|=/);
        const length = paramParts.length;

        for (let idx = 0; idx < length; idx += 2) {
            if (paramParts[idx] !== "") {
                params[decodeURIComponent(paramParts[idx])] = decodeURIComponent(paramParts[idx + 1]);
            }
        }

        return params;
    }

    /**
     * Get element under cursor position
     */
    elementUnderCursor(e: { x: { client?: number }; y: { client?: number } }): Element | null {
        if (typeof e.x.client !== "undefined") {
            return document.elementFromPoint(e.x.client!, e.y.client!);
        }
        return null;
    }

    /**
     * Get wheel delta Y from jQuery event
     */
    wheelDeltaY(jQueryEvent: JQuery.MouseEventBase): number | undefined {
        const e = jQueryEvent.originalEvent as WheelEvent & {
            wheelDelta?: number;
            wheelDeltaY?: number;
            axis?: number;
            VERTICAL_AXIS?: number;
        };
        const deltaY = e.wheelDeltaY;
        let delta: number | undefined;

        if (e.wheelDelta) {
            // Webkit and IE
            if (deltaY === undefined || deltaY) {
                // IE does not have deltaY, thus always scroll
                delta = e.wheelDelta;
            }
        } else if (e.detail && e.axis === e.VERTICAL_AXIS) {
            // Firefox and Opera
            delta = (-e.detail) * 10;
        }

        return delta;
    }

    /**
     * Get box-shadow dimensions from an element
     */
    getShadows(element: JQuery): ShadowDimensions {
        const shadow = element.css("box-shadow");
        const radius = shadow
            ? BOX_SHADOW_REGEXP.exec(shadow) || [0, 0, 0, 0, 0]
            : [0, 0, 0, 0, 0];
        const blur = Math.max(+radius[3], +(radius[4] || 0));

        return {
            left: (-radius[1]) + blur,
            right: (+radius[1]) + blur,
            bottom: (+radius[2]) + blur
        };
    }

    /**
     * Wrap element with animation container
     */
    wrap(
        element: JQuery,
        autosize?: boolean,
        resize?: boolean,
        shouldCorrectWidth: boolean = true,
        autowidth?: boolean
    ): JQuery {
        let parent = element.parent();
        const windowOuterWidth = this.outerWidth(window);

        parent.parent().removeClass("k-animation-container-sm");

        if (!parent.hasClass("k-child-animation-container")) {
            const width = element[0].style.width;
            const height = element[0].style.height;
            const percentWidth = PERCENT_REGEXP.test(width);
            const percentHeight = PERCENT_REGEXP.test(height);
            const forceDimensions = element.hasClass("k-tooltip") || element.is(".k-menu-horizontal.k-context-menu");
            const calculateFromHidden = element.hasClass("k-tooltip");

            const percentage = percentWidth || percentHeight;

            let computedWidth: string | number = width;
            let computedHeight: string | number = height;

            if (!percentWidth && (!autosize || (autosize && width) || forceDimensions)) {
                computedWidth = autosize
                    ? this.outerWidth(element, false, calculateFromHidden) + 1
                    : this.outerWidth(element, false, calculateFromHidden);
            }
            if (!percentHeight && (!autosize || (autosize && height)) || forceDimensions) {
                computedHeight = this.outerHeight(element, false, calculateFromHidden);
            }

            element.wrap(
                $("<div/>")
                    .addClass("k-child-animation-container")
                    .css({
                        width: autowidth ? "auto" : computedWidth,
                        height: computedHeight
                    })
            );
            parent = element.parent();

            parent.wrap(
                $("<div/>")
                    .addClass("k-animation-container")
                    .attr("role", "region")
            );

            if (percentage) {
                element.css({
                    width: "100%",
                    height: "100%"
                });
            }
        } else {
            this.wrapResize(element, autosize, shouldCorrectWidth);
        }

        parent = parent.parent();

        if (windowOuterWidth < this.outerWidth(parent)) {
            parent.addClass("k-animation-container-sm");
            resize = true;
        }

        if (resize) {
            this.wrapResize(element, autosize, shouldCorrectWidth);
        }

        return parent;
    }

    /**
     * Resize wrapped element
     */
    wrapResize(element: JQuery, autosize?: boolean, shouldCorrectWidth: boolean = true): void {
        const parent = element.parent();
        const wrapper = element.closest(".k-animation-container");
        const calculateFromHidden = element.hasClass("k-tooltip");
        const visible = element.is(":visible");
        const wrapperStyle = parent[0].style;
        const elementHeight = element[0].style.height;

        if (wrapper.is(":hidden")) {
            wrapper.css({
                display: "",
                position: ""
            });
        }

        const percentage = PERCENT_REGEXP.test(wrapperStyle.width) || PERCENT_REGEXP.test(wrapperStyle.height);

        if (!percentage) {
            if (!visible) {
                element.add(parent).show();
            }
            if (shouldCorrectWidth) {
                parent.css("width", ""); // Needed to get correct width dimensions
            }
            parent.css({
                width: autosize
                    ? this.outerWidth(element, false, calculateFromHidden) + 1
                    : this.outerWidth(element, false, calculateFromHidden),
            });

            if (elementHeight === "auto") {
                element.css({ height: this.outerHeight(parent) });
            } else {
                parent.css({
                    height: this.outerHeight(element)
                });
            }

            if (!visible) {
                element.hide();
            }
        }
    }

    /**
     * Scroll element horizontally by a delta
     */
    scrollByDelta(element: JQuery, delta: number): ScrollResult {
        const isRtl = supportService.isRtl(element);
        const srcOffset = isRtl ? -this.scrollLeft(element) : this.scrollLeft(element) as number;
        const scrollDestination = srcOffset + delta;
        const scrollWidth = element[0].scrollWidth - element[0].clientWidth;

        const animationProps = { "scrollLeft": scrollDestination };

        element.finish().animate(animationProps, "fast", "linear");

        const maxScroll = isRtl ? -scrollWidth : scrollWidth;
        const newScrollLeft = isRtl
            ? Math.max(Math.min(scrollDestination, 0), maxScroll)
            : Math.min(Math.max(scrollDestination, 0), maxScroll);

        return {
            atStart: newScrollLeft === 0,
            atEnd: newScrollLeft === maxScroll
        };
    }

    /**
     * Scroll element vertically by a delta
     */
    scrollVerticalByDelta(
        element: JQuery,
        delta: number,
        options: VerticalScrollOptions = {}
    ): VerticalScrollResult {
        const { duration = 'fast', easing = 'linear' } = options;
        const currentScrollTop = element.scrollTop() || 0;
        const targetScrollTop = currentScrollTop + delta;

        const animationProps = { "scrollTop": targetScrollTop };

        element.finish().animate(animationProps, duration as number, easing);

        const maxScroll = element[0].scrollHeight - element[0].clientHeight;
        const newScrollTop = Math.min(Math.max(targetScrollTop, 0), maxScroll);

        return {
            atTop: newScrollTop === 0,
            atBottom: newScrollTop >= maxScroll
        };
    }

    /**
     * Smoothly scroll to a specific element within a container
     */
    scrollToElement(
        container: JQuery,
        targetElement: JQuery,
        options: ScrollToElementOptions = {}
    ): boolean {
        if (!container.length || !targetElement.length) {
            return false;
        }

        const {
            duration = 0,
            easing = 'linear',
            position = 'center',
            offset = 0,
            onComplete
        } = options;

        const containerHeight = container.height() || 0;
        const containerScrollTop = container.scrollTop() || 0;
        const containerOffset = container.offset()!;
        const targetOffset = targetElement.offset()!;
        const targetHeight = targetElement.outerHeight() || 0;

        const relativeTop = targetOffset.top - containerOffset.top + containerScrollTop;

        let targetScrollTop: number;

        switch (position) {
            case 'top':
                targetScrollTop = relativeTop + offset;
                break;
            case 'bottom':
                targetScrollTop = relativeTop - containerHeight + targetHeight + offset;
                break;
            case 'center':
            default:
                targetScrollTop = relativeTop - (containerHeight / 2) + (targetHeight / 2) + offset;
                break;
        }

        const maxScroll = container[0].scrollHeight - container[0].clientHeight;
        targetScrollTop = Math.min(Math.max(targetScrollTop, 0), maxScroll);

        container.finish().animate({
            scrollTop: targetScrollTop
        }, duration, easing, onComplete);

        return true;
    }

    /**
     * Add a value to an element's attribute, avoiding duplicates
     * If the value already exists in the attribute, it won't be added again
     */
    addAttribute(element: JQuery, attribute: string, value: string): void {
        const current = element.attr(attribute) || "";

        if (current.indexOf(value) < 0) {
            element.attr(attribute, (current + " " + value).trim());
        }
    }

    /**
     * Remove an attribute from an element
     */
    removeAttribute(element: JQuery, attribute: string): void {
        element.removeAttr(attribute);
    }

    /**
     * Toggle an attribute value on an element
     * For regular attributes: adds if not present, removes if present
     * For disabled/readonly: adds only when value is truthy
     */
    toggleAttribute(element: JQuery, attribute: string, value: string): void {
        const doesNotHaveAttribute = (element.attr(attribute) || "").indexOf(value) < 0;
        const disabledReadonly = ["disabled", "readonly"].indexOf(attribute) > -1;

        if (doesNotHaveAttribute && !disabledReadonly) {
            this.addAttribute(element, attribute, value);
        } else if (disabledReadonly && value) {
            this.addAttribute(element, attribute, value);
        } else {
            this.removeAttribute(element, attribute);
        }
    }

    /**
     * Bind a callback to the window resize event.
     * On Android, the callback is delayed by 600ms to handle orientation changes properly.
     * @param callback - Function to call on resize
     * @returns The handler function (may be wrapped on Android)
     */
    onResize(callback: () => void): () => void {
        let handler = callback;

        if (supportService.mobileOS && supportService.mobileOS.android) {
            handler = function () {
                setTimeout(callback, 600);
            };
        }

        $(window).on(supportService.resize, handler);
        return handler;
    }

    /**
     * Unbind a resize callback from the window.
     * @param callback - The handler returned from onResize
     */
    unbindResize(callback: () => void): void {
        $(window).off(supportService.resize, callback);
    }

    /**
     * Get a data attribute value from an element using kendo namespace.
     * @param element - jQuery element
     * @param key - Attribute key (without kendo namespace prefix)
     * @returns The attribute value
     */
    attrValue(element: JQuery, key: string): any {
        return element.data(namespaceService.ns + key);
    }

    /**
     * Get the kendo data attribute name with namespace prefix.
     * @param value - The attribute name without prefix
     * @returns The full attribute name (e.g., "data-kendo-role" or "data-role")
     */
    attr(value: string): string {
        return "data-" + namespaceService.ns + value;
    }

    /**
     * Get or set element dimensions.
     * @param element - jQuery element
     * @param dimensions - Optional dimensions to set
     * @returns Object with width and height
     */
    dimensions(element: JQuery, dimensions?: { width?: number | string; height?: number | string }): { width: number; height: number } {
        const domElement = element[0] as HTMLElement;

        if (dimensions) {
            element.css(dimensions);
        }

        return { width: domElement.offsetWidth, height: domElement.offsetHeight };
    }

    /**
     * Check if an event was triggered by an input element
     * @param e - Event object
     * @returns True if event target is a form input element
     */
    triggeredByInput(e: Event | { target: HTMLElement }): boolean {
        return (/^(label|input|textarea|select)$/i).test((e.target as HTMLElement).tagName);
    }

    /**
     * Apply inline styles from kendo data attributes to elements
     * @param element - Container element to search within
     * @param styleProps - Array of CSS property names to apply
     */
    applyStylesFromKendoAttributes(element: JQuery, styleProps: string[]): void {
        const selector = styleProps.map(styleProp => `[${this.attr(`style-${styleProp}`)}]`).join(",");
        element.find(selector).addBack(selector).each((_: number, currentElement: HTMLElement) => {
            const $currentElement = $(currentElement);
            styleProps.forEach((styleProp: string) => {
                const kendoAttr = this.attr(`style-${styleProp}`);
                if ($currentElement.attr(kendoAttr)) {
                    $currentElement.css(styleProp, $currentElement.attr(kendoAttr)!);
                    $currentElement.removeAttr(kendoAttr);
                }
            });
        });
    }

    /**
     * Show or hide a loading mask on a container element
     * @param container - Container element for the mask
     * @param toggle - Whether to show (true) or hide (false) the mask
     * @param options - Optional configuration for the mask
     */
    progress(container: JQuery, toggle: boolean, options?: ProgressOptions): void {
        let mask = container.find(".k-loading-mask");
        const browser = supportService.browser;

        const opts = $.extend({}, {
            width: "100%",
            height: "100%",
            top: container.scrollTop(),
            opacity: false
        }, options);

        const cssClass = opts.opacity ? "k-loading-mask k-opaque" : "k-loading-mask";

        if (toggle) {
            if (!mask.length) {
                const isRtl = supportService.isRtl(container);
                const leftRight = isRtl ? "right" : "left";
                const containerScrollLeft = this.scrollLeft(container) as number;
                let webkitCorrection = 0;
                if (browser.webkit && isRtl) {
                    webkitCorrection = container[0].scrollWidth - (container.width() || 0) - 2 * containerScrollLeft;
                }

                // Note: The loading text message is set via kendo.ui.progress.messages.loading
                // Sadly we must preserve this global for backward compatibility.
                const loadingText = ((window as any).kendo.ui?.progress?.messages?.loading) || "Loading...";

                $(`<div class='${cssClass}'><span role='alert' aria-live='polite' class='k-loading-text'>${loadingText}</span><div class='k-loading-image'></div><div class='k-loading-color'></div></div>`)
                    .width(opts.width!)
                    .height(opts.height!)
                    .css("top", opts.top!)
                    .css(leftRight, Math.abs(containerScrollLeft) + webkitCorrection)
                    .prependTo(container);
            }
        } else if (mask) {
            mask.remove();
        }
    }

    /**
     * Get the actual target element from an event, handling touch events.
     * For touch events, uses document.elementFromPoint with touch coordinates.
     * @param e - Event object (mouse or touch)
     * @returns The target element
     */
    eventTarget(e: Event | JQuery.Event): EventTarget | null {
        if (!supportService.touch) {
            return (e as Event).target;
        }

        // Handle touch events - get element at touch point
        const originalEvent = (e as JQuery.TouchEventBase).originalEvent as TouchEvent | undefined;
        const touches = originalEvent?.changedTouches || (e as unknown as TouchEvent).changedTouches;

        if (touches && touches.length > 0) {
            return document.elementFromPoint(touches[0].clientX, touches[0].clientY);
        }

        return (e as Event).target;
    }

    /**
     * Create a drag-to-scroll handler for horizontal scrolling via mouse/touch drag.
     * Encapsulates the drag state and event handling for scrollable containers.
     * @param scrollContainer - The element that will be scrolled (or parent for delegation)
     * @param options - Configuration options including namespace, capture element, and delegate selector
     * @returns Handler with attach() and destroy() methods
     */
    createDragToScrollHandler(scrollContainer: JQuery, options: DragToScrollOptions): DragToScrollHandler {
        return new DragToScrollHandlerImpl(scrollContainer, options);
    }

    /**
     * Create a ResizeObserver service for monitoring element resize events.
     * Provides debounced resize callbacks and automatic cleanup.
     * @param options - Configuration options including element, callback, and debounce time
     * @returns ResizeObserverService instance with destroy() method
     */
    createResizeObserver(options: ResizeObserverServiceOptions): ResizeObserverService {
        return new ResizeObserverService(options);
    }
}

class DragToScrollHandlerImpl implements DragToScrollHandler {
    private static readonly DRAG_THRESHOLD = 5;

    private scrollContainer: JQuery;
    private namespace: string;
    private captureElement?: JQuery;
    private delegateSelector?: string;

    private isDragging = false;
    private hasDragged = false;
    private dragStartX = 0;
    private scrollStartLeft = 0;
    private currentDragTarget: HTMLElement | null = null;

    constructor(scrollContainer: JQuery, options: DragToScrollOptions) {
        this.scrollContainer = scrollContainer;
        this.namespace = options.namespace;
        this.captureElement = options.captureElement;
        this.delegateSelector = options.delegateSelector;

        this.onDragStart = this.onDragStart.bind(this);
        this.onDragMove = this.onDragMove.bind(this);
        this.onDragEnd = this.onDragEnd.bind(this);
        this.preventClickOnce = this.preventClickOnce.bind(this);
    }

    attach(): void {
        if (!this.captureElement) {
            return;
        }

        if (this.delegateSelector) {
            this.scrollContainer
                .on("mousedown" + this.namespace, this.delegateSelector, this.onDragStart)
                .on("touchstart" + this.namespace, this.delegateSelector, this.onDragStart);
        } else {
            this.scrollContainer
                .on("mousedown" + this.namespace, this.onDragStart)
                .on("touchstart" + this.namespace, this.onDragStart);
        }

        this.bindCaptureEvents();
    }

    destroy(): void {
        this.scrollContainer.off(this.namespace);
        if (this.captureElement) {
            this.unbindCaptureEvents();
        }
        this.scrollContainer[0].removeEventListener("click", this.preventClickOnce, true);
    }

    private getClientX(e: any): number {
        if (e.type.indexOf("touch") !== -1) {
            const touch = e.originalEvent?.touches?.[0] || e.originalEvent?.changedTouches?.[0];
            return touch ? touch.clientX : 0;
        }
        return e.clientX || e.originalEvent?.clientX || 0;
    }

    private preventClickOnce(e: MouseEvent): void {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.scrollContainer[0].removeEventListener("click", this.preventClickOnce, true);
    }

    private onDragStart(e: any): void {
        const target = $(e.currentTarget);
        if (!target.length) {
            return;
        }

        this.isDragging = true;
        this.hasDragged = false;
        this.currentDragTarget = target[0];
        this.dragStartX = this.getClientX(e);
        this.scrollStartLeft = target[0].scrollLeft;

        target.css("cursor", "grabbing");
        target.css("user-select", "none");
    }

    private onDragMove(e: any): void {
        if (!this.isDragging || !this.currentDragTarget) {
            return;
        }

        const clientX = this.getClientX(e);
        const deltaX = this.dragStartX - clientX;

        if (!this.hasDragged && Math.abs(deltaX) >= DragToScrollHandlerImpl.DRAG_THRESHOLD) {
            this.hasDragged = true;
        }

        if (this.hasDragged) {
            e.preventDefault();
            this.currentDragTarget.scrollLeft = this.scrollStartLeft + deltaX;
        }
    }

    private onDragEnd(): void {
        if (!this.isDragging) {
            return;
        }

        if (this.currentDragTarget) {
            const target = $(this.currentDragTarget);
            target.css("cursor", "");
            target.css("user-select", "");
        }

        if (this.hasDragged) {
            this.scrollContainer[0].addEventListener("click", this.preventClickOnce, true);
        }

        this.isDragging = false;
        this.hasDragged = false;
        this.currentDragTarget = null;
    }

    private bindCaptureEvents(): void {
        if (!this.captureElement) {
            return;
        }

        // Bind all events to document so dragging continues when cursor leaves component
        $(document)
            .on("mousemove" + this.namespace, this.onDragMove)
            .on("touchmove" + this.namespace, this.onDragMove)
            .on("mouseup" + this.namespace, this.onDragEnd)
            .on("touchend" + this.namespace, this.onDragEnd);
    }

    private unbindCaptureEvents(): void {
        $(document).off(this.namespace);
    }
}

const DEFAULT_RESIZE_EVENT_TIME = 50;
const HAS_OBSERVER = typeof ResizeObserver !== "undefined";

/**
 * @hidden
 */
class ResizeObserverService {
    element: HTMLElement | null;
    private resizeCallback: (() => void) | null;
    private debounceTime: number;
    private observeOptions: ResizeObserverOptions;
    private resizeObserver: ResizeObserver | null;
    private debounceResize: ((...args: any[]) => void) & { cancel(): void } | null;

    static supported(): boolean {
        return HAS_OBSERVER;
    }

    constructor(options: ResizeObserverServiceOptions) {
        const el = options.element;
        this.element = el instanceof $ ? (el as JQuery)[0] : el as HTMLElement;
        this.resizeCallback = options.onResize;
        this.debounceTime = options.debounceTime ?? DEFAULT_RESIZE_EVENT_TIME;
        this.observeOptions = options.observeOptions || {};
        this.resizeObserver = null;
        this.debounceResize = null;

        this.initResizeObserver();
    }

    private initResizeObserver(): void {
        this.debounceResize = utilsService.throttle(this.onResize.bind(this), this.debounceTime) as any;

        if (HAS_OBSERVER) {
            this.resizeObserver = new ResizeObserver(this.debounceResize!);
            this.resizeObserver.observe(this.element!, this.observeOptions);
        }
    }

    private onResize = (_entries?: ResizeObserverEntry[]): void => {
        if (this.resizeCallback) {
            this.resizeCallback();
        }
    };

    destroy(): void {
        this.destroyResizeObserver();
        this.element = null;
    }

    private destroyResizeObserver(): void {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }

        if (this.debounceResize) {
            this.debounceResize.cancel();
        }

        this.resizeObserver = null;
        this.debounceResize = null;
        this.resizeCallback = null;
    }
}

export const domUtilsService = new DomUtilsService();